import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Calendar, MapPin, Clock, Check } from 'lucide-react';
import { FaWhatsapp } from "react-icons/fa";

export const TourDetailPage = ({ tour }) => {
  if (!tour) {
    return (
      <div>
        <Navigation />
        <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="hero-large">Tour Not Found</h1>
        </div>
        <Footer />
      </div>
    );
  }

  // Determine back link based on tour category, continent, region, and geographic group
  const getBackLink = () => {
    if (tour.category === 'worldwide' && tour.continent && tour.region) {
      const regionNames = {
        'south-asia': 'South Asia',
        'east-asia': 'East Asia',
        'southeast-asia': 'Southeast Asia',
        'central-asia': 'Central Asia',
        'middle-east': 'Middle East',
        'western-europe': 'Western Europe',
        'central-europe': 'Central Europe',
        'northern-europe': 'Northern Europe',
        'southern-europe': 'Southern Europe',
        'eastern-europe': 'Eastern Europe',
        'north-africa': 'North Africa',
        'east-africa': 'East Africa',
        'southern-africa': 'Southern Africa',
        'west-africa': 'West Africa',
        'north-america': 'North America',
        'central-america': 'Central America',
        'south-america': 'South America',
        'australia-new-zealand': 'Australia & New Zealand',
        'melanesia': 'Melanesia',
        'micronesia': 'Micronesia',
        'polynesia': 'Polynesia'
      };
      return {
        path: `/worldwide-journeys/${tour.continent}/${tour.region}`,
        label: `← Back to ${regionNames[tour.region] || 'Worldwide Journeys'}`
      };
    }
    if (tour.category === 'indonesia' && tour.geographicGroup) {
      const groupNames = {
        'sumatra': 'Sumatra',
        'java': 'Java',
        'bali-nusa-tenggara': 'Bali & Nusa Tenggara',
        'kalimantan': 'Kalimantan',
        'sulawesi': 'Sulawesi',
        'maluku': 'Maluku',
        'papua': 'Papua'
      };
      return {
        path: `/indonesia-journeys/${tour.geographicGroup}`,
        label: `← Back to ${groupNames[tour.geographicGroup] || 'Indonesia Journeys'}`
      };
    }
    return {
      path: '/indonesia-journeys',
      label: '← Back to Indonesia Journeys'
    };
  };

  const backLink = getBackLink();

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '60vh',
        minHeight: '500px',
        background: `linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.12)), url(${tour.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="container" style={{ textAlign: 'center', color: 'white', zIndex: 2 }}>
          <div style={{ marginBottom: '24px' }}>
  <Link
    to={backLink.path}
    style={{
      color: 'var(--text-secondary)',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease'
    }}
    className="back-link"
  >
    {backLink.label}
  </Link>
</div>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {tour.title}
          </h1>
          <p className="body-large" style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '24px' }}>
            {tour.subtitle}
          </p>
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            color: 'white',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPin size={18} />
              <span>{tour.destination}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} />
              <span>{tour.duration}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} />
              <span>{tour.dates}</span>
            </div>
          </div>
          <a
            href={tour.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: '#25D366',
              color: 'white',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: 0,
              transition: 'background 0.2s'
            }}
            className="whatsapp-cta"
          >
            <FaWhatsapp size={20} />
            Book This Journey via WhatsApp
          </a>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 className="hero-medium" style={{ textAlign: 'center', marginBottom: '48px' }}>
              Journey Highlights
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {tour.highlights.map((highlight, index) => (
                <div
                  key={`highlight-${index}-${highlight.substring(0, 20)}`}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '20px',
                    background: 'var(--bg-secondary)',
                    borderRadius: 0
                  }}
                >
                  <Check size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="hero-medium" style={{ textAlign: 'center', marginBottom: '16px' }}>
              Detailed Itinerary
            </h2>
            {tour.flight && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'var(--bg-primary)',
                marginBottom: '48px',
                borderRadius: 0
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  <strong>Flight:</strong> {tour.flight}
                </p>
              </div>
            )}
            {tour.ship && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'var(--bg-primary)',
                marginBottom: '48px',
                borderRadius: 0
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  <strong>Ship:</strong> {tour.ship}
                </p>
              </div>
            )}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              {tour.itinerary.map((day, index) => (
                <div
                  key={day.day ? `day-${day.day}` : `itinerary-${index}-${day.title}`}
                  style={{
                    background: 'var(--bg-primary)',
                    padding: '32px',
                    borderRadius: 0,
                    borderLeft: '3px solid var(--text-primary)'
                  }}
                >
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                    {day.day && (
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'var(--text-primary)',
                        color: 'var(--bg-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        {day.day}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                        <h3 className="heading-3">{day.title}</h3>
                        {day.date && (
                          <span className="body-small" style={{
                            padding: '4px 12px',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)',
                            borderRadius: 0
                          }}>
                            {day.date}
                          </span>
                        )}
                      </div>
                      <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                        {day.activities}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)', textAlign: 'center' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
            Ready to Embark on This Journey?
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px', maxWidth: '700px', margin: '0 auto 32px' }}>
            Contact us directly via WhatsApp for immediate assistance, custom itineraries, and special arrangements.
          </p>
          <a
            href={tour.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px 40px',
              background: '#25D366',
              color: 'white',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: 0,
              transition: 'background 0.2s'
            }}
            className="whatsapp-cta"
          >
            <FaWhatsapp size={20} />
            Inquire via WhatsApp Now
          </a>
        </div>
      </section>

      <Footer />

      <style>{`
        .whatsapp-cta:hover {
          background: #20BA5A !important;
        }
      `}</style>
    </div>
  );
};
